// ComparisonAccordion.js
import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { useState, useEffect, memo } from 'react';
import { styled, alpha } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import _ from 'lodash';
import StatusCell from './StatusCell';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateNotes,
  updateManualBeaconStatus,
  addExtraEventToTestCaseAsync,
  markEventAsAdded,
  selectAddedEvents,
} from '../../redux/validation/validationResultsSlice';
import AddExtraEventDialog from './AddExtraEventDialog';

// Define internal metadata fields that should not be displayed in tables
const INTERNAL_METADATA = [
  'event_name',
  'event_direction',
  'timestamp',
  'device_id',
  'is_blocked_event',
  'market_id',
  'market_name',
];

// Comparison function
function compareFields(truthData, actualData) {
  // For flow-based validation, we compare expected attributes with actual flattened data
  return _.every(truthData, (truthValue, key) => {
    if (truthValue && truthValue.value !== undefined) {
      // Only compare static fields for overall pass/fail determination
      if (truthValue.fieldType && truthValue.fieldType === 'Static') {
        const actualValue = actualData[key];
        return _.isEqual(String(truthValue.value), String(actualValue));
      }
    }
    return true;
  });
}

const CustomToolBar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: '0px solid transparent',
  borderRadius: '8px',
  width: '100%',
  '& .MuiDataGrid-root': {
    width: '100%',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'custom.surfaceAlt',
    color: 'text.primary',
    fontWeight: 'bold',
    borderBottom: '0px solid transparent',
    minWidth: 0,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '0px solid transparent',
    padding: '8px 16px',
    overflow: 'hidden',
  },
  '& .MuiDataGrid-row': {
    minHeight: '60px',
    '&:nth-of-type(odd)': {
      backgroundColor: 'custom.surfaceAlt',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '0px solid transparent',
  },
  '& .MuiPaginationItem-root': {
    borderRadius: '50%',
    margin: '0 4px',
  },
  // Optimize virtualization and scrolling
  '& .MuiDataGrid-virtualScroller': {
    overflowX: 'hidden',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    minHeight: 'unset !important',
  },

  '& .word-wrap': {
    whiteSpace: 'normal !important',
    wordBreak: 'break-word !important',
    overflowWrap: 'break-word !important',
    lineHeight: '1.3em',
    display: 'block !important',
  },

  '& .word-wrap .MuiDataGrid-cellContent': {
    whiteSpace: 'normal !important',
    wordBreak: 'break-word !important',
    overflowWrap: 'break-word !important',
    display: 'block !important',
  },
}));

const columns = [
  {
    field: 'variableName',
    headerName: 'Variable Name',
    flex: 0.5,
    cellClassName: 'word-wrap',
    renderCell: (params) => {
      // Alternative approach: explicitly check for Extra status
      const shouldBeBold = params.row.isFlowAttribute && params.row.passFail !== 'Extra';

      // Color extra custom_attributes red
      const isCustomAttribute = params.row.variableName.startsWith('data.custom_attributes.');
      const isExtra = params.row.passFail === 'Extra';
      const shouldBeRed = isCustomAttribute && isExtra;

      return (
        <Box
          sx={{
            fontWeight: shouldBeBold ? 'bold' : 'normal !important',
            color: shouldBeRed ? 'error.main' : 'inherit',
          }}
        >
          {params.value}
        </Box>
      );
    },
  },
  {
    field: 'expectedValue',
    headerName: 'Expected Value',
    flex: 1,
    cellClassName: 'word-wrap',
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'pre-wrap' }}>
        {params.value === null ? 'null' : String(params.value)}
      </Box>
    ),
  },
  {
    field: 'actualValueMP',
    headerName: 'Actual Value (mParticle)',
    flex: 1,
    cellClassName: 'word-wrap',
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'pre-wrap' }}>
        {params.value === null ? 'null' : String(params.value)}
      </Box>
    ),
  },
  {
    field: 'actualValue',
    headerName: 'Actual Value (GMA)',
    flex: 1,
    cellClassName: 'word-wrap',
    renderCell: (params) => (
      <Box sx={{ whiteSpace: 'pre-wrap' }}>
        {params.value === null ? 'null' : String(params.value)}
      </Box>
    ),
  },
  {
    field: 'passFail',
    headerName: 'Status',
    flex: 0.2,
    renderCell: (params) => <StatusCell {...params} />,
  },
  { field: 'fieldType', headerName: 'Type', flex: 0.2 },
];

function ComparisonAccordion(props) {
  const [cardData, setCardData] = useState({});
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesText, setNotesText] = useState(props.notes || '');
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const { onRendered, readOnly = false } = props;
  const dispatch = useDispatch();
  const addedEvents = useSelector(selectAddedEvents);

  // Handler to save notes
  const handleSaveNotes = () => {
    dispatch(
      updateNotes({
        beaconIndex: props.index,
        notes: notesText,
      }),
    );
    setNotesOpen(false);
  };

  const handleCloseNotes = () => {
    setNotesOpen(false);
  };

  // Handler to add extra event to test case
  const handleAddEventToTestCase = async (options) => {
    try {
      await dispatch(
        addExtraEventToTestCaseAsync({
          eventIndex: props.index,
          options,
        }),
      ).unwrap();

      // Mark event as added
      dispatch(markEventAsAdded({ eventIndex: props.index }));
      setAddEventDialogOpen(false);
    } catch (error) {
      console.error('Failed to add event to test case:', error);
      throw error;
    }
  };

  let tableData = [];

  // Use flow comparison data if available, otherwise fall back to old comparison logic
  if (props.flowComparison && props.flowComparison.length > 0) {
    // Get the expected attribute names from the flow comparison
    const flowAttributeNames = new Set(props.flowComparison.map((result) => result.variableName));

    // Get all attributes from the actual data
    const allActualAttributes = Object.keys(props.apiData.data || {});

    // First add flow comparison results (these are flow attributes)
    props.flowComparison.forEach((result, index) => {
      tableData.push({
        id: index,
        index: props.index,
        variableName: result.variableName,
        expectedValue: result.expectedValue,
        actualValueMP: result.actualValueMP,
        actualValue: result.actualValue,
        passFail:
          props.modifiedRows && props.modifiedRows[result.variableName] !== undefined
            ? props.modifiedRows[result.variableName]
            : result.passFail,
        fieldType: result.fieldType,
        modified: props.modifiedRows && props.modifiedRows[result.variableName] !== undefined,
        isFlowAttribute: result.isFlowAttribute,
        readOnly: readOnly,
      });
    });

    // Then add any extra attributes not in the flow (these are NOT flow attributes)
    const proxyData = props.apiData.proxyData || {};
    allActualAttributes.forEach((attrName) => {
      if (!flowAttributeNames.has(attrName) && !INTERNAL_METADATA.includes(attrName)) {
        const actualValue = props.apiData.data[attrName];
        const proxyValue = proxyData[attrName];
        const extraAttrData = {
          id: tableData.length,
          index: props.index,
          variableName: attrName,
          expectedValue: '',
          actualValueMP: actualValue === null ? 'null' : String(actualValue),
          actualValue:
            proxyValue === undefined ? '' : proxyValue === null ? 'null' : String(proxyValue),
          passFail: 'Extra',
          fieldType: 'N/A',
          modified: false,
          isFlowAttribute: false, // Not from flow, so not bolded
          readOnly: readOnly,
        };

        tableData.push(extraAttrData);
      }
    });
  } else {
    // Fallback to original comparison logic for backward compatibility
    const truthData = props.apiDataTrue.data || {};
    const realData = props.apiData.data || {};
    const fallbackProxyData = props.apiData.proxyData || {};

    const allKeys = Array.from(
      new Set([...Object.keys(truthData), ...Object.keys(realData)]),
    ).filter((key) => key !== 'market');

    allKeys.forEach((key, index) => {
      const truthEntry = truthData[key];
      const realEntry = realData[key];

      const truthValue = truthEntry instanceof Object ? truthEntry.value : truthEntry;
      const fieldType = truthEntry instanceof Object ? truthEntry.fieldType : 'N/A';
      const beaconValue = realEntry;
      const proxyValue = fallbackProxyData[key];

      let computedStatus;

      if (!(key in truthData)) {
        computedStatus = 'Extra';
      } else if (!(key in realData)) {
        computedStatus = 'Missing';
      } else if (beaconValue === '') {
        computedStatus = 'Fail';
      } else if (fieldType !== 'Dynamic') {
        computedStatus = _.isEqual(truthValue, beaconValue) ? 'Pass' : 'Fail';
      } else {
        const dynamicListPattern = /\[(.*?)\]/;
        const matches = String(truthValue).match(dynamicListPattern);
        if (matches && matches[1]) {
          const valuesArr = matches[1].split(',').map((val) => val.trim());
          computedStatus =
            valuesArr.length === 1
              ? beaconValue !== ''
                ? 'Pass'
                : 'Fail'
              : valuesArr.includes(String(beaconValue).trim())
                ? 'Pass'
                : 'Fail';
        } else {
          computedStatus = 'Pass';
        }
      }

      const finalStatus =
        props.modifiedRows && props.modifiedRows[key] !== undefined
          ? props.modifiedRows[key]
          : computedStatus;

      tableData.push({
        id: index,
        index: props.index,
        variableName: key,
        expectedValue: key in truthData ? truthValue : '',
        actualValueMP: key in realData ? beaconValue : '',
        actualValue:
          proxyValue === undefined ? '' : proxyValue === null ? 'null' : String(proxyValue),
        passFail: finalStatus,
        fieldType,
        modified: props.modifiedRows && props.modifiedRows[key] !== undefined,
        isFlowAttribute: key in truthData, // Mark as flow attribute only if it exists in truth data
      });
    });
  }

  // Sort data; flow attributes first, then custom_attributes
  tableData.sort((a, b) => {
    // 1) Flow attributes MUST come first
    if (a.isFlowAttribute && !b.isFlowAttribute) return -1;
    if (!a.isFlowAttribute && b.isFlowAttribute) return 1;

    const keyA = a.variableName;
    const keyB = b.variableName;
    const prefix = 'data.custom_attributes.';

    const isCA_A = keyA.startsWith(prefix);
    const isCA_B = keyB.startsWith(prefix);

    // 2) Within same isFlowAttribute group, custom attributes first
    if (isCA_A && !isCA_B) return -1;
    if (!isCA_A && isCA_B) return 1;

    // 2) If both are custom_attributes, try numeric prop/eVar ordering
    if (isCA_A && isCA_B) {
      const re = /^data\.custom_attributes\.(prop|eVar)(\d+)$/;
      const mA = keyA.match(re);
      const mB = keyB.match(re);

      if (mA && mB) {
        const numA = parseInt(mA[2], 10);
        const numB = parseInt(mB[2], 10);
        if (numA !== numB) return numA - numB;
        if (mA[1] !== mB[1]) {
          return mA[1] === 'prop' ? -1 : 1;
        }
        return 0;
      }

      if (mA && !mB) return -1;
      if (!mA && mB) return 1;

      return keyA.localeCompare(keyB);
    }

    return 0;
  });

  useEffect(() => {
    if (onRendered) {
      onRendered();
    }
  }, [onRendered]);

  useEffect(() => {
    const statusLabelMap = {
      PASS: 'Pass',
      FAIL: 'Fail',
      MISSING: 'Missing Event',
      NOT_VALIDATED: 'Not Validated',
      NOT_PRESENT_IN_APP: 'Not Present In App',
    };

    // ONLY MANUAL OVERRIDE
    if (props.manualBeaconStatus && props.manualBeaconStatus !== '') {
      setCardData({
        beacon: props.beaconID,
        status: statusLabelMap[props.manualBeaconStatus] || props.manualBeaconStatus,
        timestamp: props.timestamp,
        beaconType: props.beaconType,
      });

      return;
    }

    if (props.apiDataTrue.status === 404 || props.apiData.status === 404) {
      setCardData({
        beacon: props.beaconID,
        status: props.isMissing ? 'Missing Event' : 'Not Validated',
        timestamp: props.timestamp,
        beaconType: props.beaconType,
      });
    } else if (props.apiDataTrue.status === 200 && props.apiData.status === 200) {
      // Both truth (flow) and actual data are available
      let overallStatus = 'Pass';

      if (props.flowComparison) {
        // Use flow comparison results to determine overall status
        const hasFailures = props.flowComparison.some(
          (result) => result.passFail === 'Fail' || result.passFail === 'Missing',
        );

        overallStatus = hasFailures ? 'Fail' : 'Pass';
      } else {
        // Fallback to old comparison logic
        overallStatus = compareFields(props.apiDataTrue.data, props.apiData.data) ? 'Pass' : 'Fail';
      }

      setCardData({
        beacon: props.beaconID,
        status: overallStatus,
        timestamp: props.timestamp,
        beaconType: props.beaconType,
      });
    }
  }, [
    props.apiDataTrue,
    props.apiData,
    props.beaconID,
    props.timestamp,
    props.beaconType,
    props.manualBeaconStatus,
    props.flowComparison,
    props.isMissing,
  ]);

  return (
    <>
      {cardData.status ? (
        <Accordion
          sx={{
            width: '100%',
            maxWidth: '100%',
            marginBottom: '10px',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            '& .MuiAccordionSummary-root': {
              minWidth: 0,
              overflow: 'hidden',
            },
            '& .MuiCollapse-root': {
              transitionDuration: '250ms !important',
            },
            '& .MuiAccordionDetails-root': {
              padding: '8px 16px 16px',
            },
          }}
          TransitionProps={{
            unmountOnExit: true,
            timeout: 250,
          }}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                pr: 2,
                alignItems: 'center',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                gap: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                  minWidth: 0,
                  flex: '1 1 auto',
                  display: 'flex',
                  gap: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {props.stepNumber ? (
                    <Box
                      sx={{
                        backgroundColor: props.hasOrderIssue ? 'warning.light' : 'primary.main',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                        fontWeight: '600',
                      }}
                    >
                      Event {props.stepNumber}
                    </Box>
                  ) : (
                    !props.isMissing &&
                    !props.flowComparison && (
                      <Box
                        sx={{
                          backgroundColor: 'info.main',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75em',
                          fontWeight: '600',
                        }}
                      >
                        Extra
                      </Box>
                    )
                  )}
                  {/* Beacon Type Badge */}
                  <Box
                    sx={{
                      backgroundColor:
                        cardData.beaconType === 'inbound' ? 'transparent' : 'transparent', // All types now use transparent bg
                      color: cardData.beaconType === 'inbound' ? 'success.light' : 'text.secondary',
                      border: (theme) =>
                        cardData.beaconType === 'inbound'
                          ? `1px solid ${theme.palette.success.light}`
                          : `1px solid ${theme.palette.text.secondary}`,
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.75em',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                    }}
                  >
                    {cardData.beaconType === 'epsilon'
                      ? '↗ epsilon'
                      : cardData.beaconType === 'kochava'
                        ? '↗ kochava'
                        : cardData.beaconType === 'mixpanel'
                          ? '↗ mixpanel'
                          : cardData.beaconType === 'inbound'
                            ? '↓ inbound'
                            : cardData.beaconType || 'unknown'}
                  </Box>
                  {/* Blocked Event Badge */}
                  {props.isBlockedEvent && (
                    <Box
                      sx={{
                        backgroundColor: 'error.main',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                      }}
                    >
                      Blocked Event
                    </Box>
                  )}
                  {props.eventName ? `${props.eventName}` : `Beacon ${cardData.beacon}`}
                </Box>
                {/* Event name line */}
                <Box
                  sx={{
                    fontSize: '0.85em',
                    color: 'text.secondary',
                    paddingLeft: '4px',
                  }}
                >
                  {props.stepName || ''}
                </Box>
                {/* Out of order warning */}
                <Box
                  sx={{
                    color: props.hasOrderIssue ? 'warning.light' : 'transparent',
                    fontSize: '0.85em',
                    fontWeight: 'normal',
                    minHeight: props.hasOrderIssue ? '24px' : '0px',
                    display: props.hasOrderIssue ? 'flex' : 'none',
                    alignItems: 'center',
                  }}
                  title={
                    props.hasOrderIssue
                      ? `Expected at step #${props.stepNumber || 'unknown'}, but received at position #${props.actualSequence || 'unknown'}`
                      : ''
                  }
                >
                  {props.hasOrderIssue
                    ? `⚠️ Out of Order (Actual: #${props.actualSequence || '?'})`
                    : ''}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {/* Add to Test Case button - show for all events with actual data in non-readOnly mode */}
                {!props.isMissing &&
                  props.apiData &&
                  !readOnly &&
                  (addedEvents[props.index] ? (
                    <Box
                      sx={{
                        backgroundColor: 'success.light',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      ✓ Added
                    </Box>
                  ) : (
                    <Box
                      component="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setAddEventDialogOpen(true);
                      }}
                      style={{
                        background: 'none',
                        border: (theme) => `2px solid ${theme.palette.info.main}`,
                        cursor: 'pointer',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        color: 'info.main',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                      }}
                    >
                      + Add to Test Case
                    </Box>
                  ))}

                {/* NOTES button - show in live mode always, or in history mode only if notes exist */}
                {(!readOnly || (readOnly && props.notes && props.notes.trim() !== '')) && (
                  <Box
                    component="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setNotesOpen(true);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      color: 'success.light',
                    }}
                  >
                    Notes
                  </Box>
                )}

                {/* Timestamp */}
                <Box
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    visibility: props.isMissing ? 'hidden' : 'visible',
                    display: { xs: 'none', md: 'block' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {new Date(cardData.timestamp).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    fractionalSecondDigits: 3,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  })}{' '}
                  &nbsp;{' '}
                  {new Date(cardData.timestamp).toLocaleDateString('en-US', {
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  })}
                </Box>

                <Chip
                  label={
                    cardData.status === 'NOT_PRESENT_IN_APP'
                      ? 'NOT PRESENT IN APP'
                      : cardData.status.replaceAll('_', ' ')
                  }
                  onClick={(e) => {
                    e.stopPropagation();

                    if (readOnly) return;

                    const statuses = ['PASS', 'FAIL', 'NOT_PRESENT_IN_APP'];

                    const currentStatus = (
                      props.manualBeaconStatus || cardData.status
                    )?.toUpperCase();

                    const currentIndex = statuses.indexOf(currentStatus);

                    const nextStatus = statuses[(currentIndex + 1) % statuses.length];

                    // HISTORY PAGE
                    if (props.historyMode) {
                      props.onValidationStatusChange(props.index, nextStatus);
                      return;
                    }

                    // LIVE VALIDATION PAGE
                    dispatch(
                      updateManualBeaconStatus({
                        beaconIndex: props.index,
                        newStatus: nextStatus,
                      }),
                    );
                  }}
                  sx={{
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    width: '150px',
                    display: 'flex',
                    justifyContent: 'center',
                    fontFamily: 'Poppins, sans-serif',
                    cursor: !readOnly ? 'pointer' : 'default',

                    backgroundColor: (theme) =>
                      cardData.status === 'PASS'
                        ? alpha(theme.palette.success.main, 0.15)
                        : cardData.status === 'FAIL'
                          ? alpha(theme.palette.error.main, 0.15)
                          : cardData.status === 'NOT_PRESENT_IN_APP'
                            ? alpha(theme.palette.warning.main, 0.18)
                            : alpha(theme.palette.text.secondary, 0.15),

                    color:
                      cardData.status === 'PASS'
                        ? 'success.main'
                        : cardData.status === 'FAIL'
                          ? 'error.main'
                          : cardData.status === 'NOT_PRESENT_IN_APP'
                            ? 'warning.main'
                            : 'text.primary',

                    border: 'none',

                    '&:hover': {
                      backgroundColor: (theme) =>
                        cardData.status === 'PASS'
                          ? alpha(theme.palette.success.main, 0.25)
                          : cardData.status === 'FAIL'
                            ? alpha(theme.palette.error.main, 0.25)
                            : cardData.status === 'NOT_PRESENT_IN_APP'
                              ? alpha(theme.palette.warning.main, 0.28)
                              : alpha(theme.palette.text.secondary, 0.25),
                    },
                  }}
                />
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            {cardData.status === 'NOT_VALIDATED' || cardData.status === 'MISSING' ? (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <StyledDataGrid
                  autoHeight
                  hideFooter
                  disableColumnResize
                  disableVirtualization
                  rows={Object.entries(props.apiData.data || {})
                    .filter(([k]) => k !== 'market')
                    .map(([k, v], i) => ({
                      id: i,
                      variableName: k,
                      value: v instanceof Object ? v.value : v,
                    }))}
                  columns={[
                    {
                      field: 'variableName',
                      headerName: 'Variable Name',
                      flex: 1,
                      cellClassName: 'word-wrap',
                    },
                    { field: 'value', headerName: 'Value', flex: 1, cellClassName: 'word-wrap' },
                  ]}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                }}
              >
                <StyledDataGrid
                  getRowClassName={(params) => `super-app-theme--${params.row.passFail}`}
                  slots={{ toolbar: CustomToolBar }}
                  getRowHeight={() => 'auto'}
                  autoHeight
                  disableColumnResize
                  disableVirtualization
                  initialState={{
                    sorting: {
                      sortModel: [], // Prevent default sorting
                    },
                  }}
                  rows={tableData}
                  columns={columns}
                  pageSize={tableData.length}
                  rowsPerPageOptions={[tableData.length]}
                  // Removed the preapplied filter to show ALL attributes
                  // initialState={{
                  //   filter: {
                  //     filterModel: {
                  //       items: [
                  //         {
                  //           field: "variableName",
                  //           operator: "contains",
                  //           value: "data.custom_attributes",
                  //         },
                  //       ],
                  //     },
                  //   },
                  // }}
                  pageSizeOptions={[50, 100]}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ) : (
        ''
      )}

      <Modal
        open={notesOpen}
        onClose={handleCloseNotes}
        aria-labelledby="notes-modal-title"
        aria-describedby="notes-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <TextField
            fullWidth
            label="Enter your notes"
            multiline
            rows={10}
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
            }}
          >
            <Button
              onClick={handleCloseNotes}
              sx={{
                borderColor: 'divider',
                color: 'error.main',
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': { borderColor: 'text.disabled' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                textTransform: 'none',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'success.light' },
              }}
              onClick={handleSaveNotes}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Add Extra Event Dialog */}
      <AddExtraEventDialog
        open={addEventDialogOpen}
        onClose={() => setAddEventDialogOpen(false)}
        event={{
          index: props.index,
          eventName: props.eventName,
          beaconType: props.beaconType || 'inbound',
          beaconData: props.apiData,
        }}
        onConfirm={handleAddEventToTestCase}
      />
    </>
  );
}

export default memo(ComparisonAccordion, (prevProps, nextProps) => {
  return (
    _.isEqual(prevProps.apiData, nextProps.apiData) &&
    _.isEqual(prevProps.apiDataTrue, nextProps.apiDataTrue) &&
    _.isEqual(prevProps.modifiedRows, nextProps.modifiedRows) &&
    _.isEqual(prevProps.flowComparison, nextProps.flowComparison) &&
    prevProps.notes === nextProps.notes &&
    prevProps.manualBeaconStatus === nextProps.manualBeaconStatus &&
    prevProps.onRendered === nextProps.onRendered &&
    prevProps.eventName === nextProps.eventName &&
    prevProps.isMissing === nextProps.isMissing &&
    prevProps.isBlockedEvent === nextProps.isBlockedEvent &&
    prevProps.historyMode === nextProps.historyMode
  );
});
